import React, { useEffect, useRef, useState } from "react";
import { authFetch } from "../../../api";
import { cityApi, countryApi, stateApi } from "../../api/coursesApi";

export default function AddressModal({ open, onClose, onSave }) {
  const dialogRef = useRef(null);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({
    fullName: "",
    country_id: "",
    state_id: "",
    city: "",
    zip: "",
    address: "",
    isDefault: true,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    if (form.country_id) fetchStates(form.country_id);
  }, [form.country_id]);

  useEffect(() => {
    if (form.state_id && form.country_id)
      fetchCities(form.state_id, form.country_id);
    else {
      setCities([]);
      setForm((f) => ({ ...f, city: "" }));
    }
  }, [form.state_id, form.country_id]);

  useEffect(() => {
    const firstName = localStorage.getItem("firstName");
    if (firstName) setForm((f) => ({ ...f, fullName: firstName }));
  }, []);

  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape") onClose?.();
    }
    if (open) document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    setErrors((err) => ({ ...err, [name]: "" })); // reset error on change
  }

  const validateForm = () => {
    const newErrors = {};
    if (!form.fullName.trim() || form.fullName.trim().length < 2)
      newErrors.fullName = "Full name must be at least 2 characters.";

    if (!form.country_id) newErrors.country_id = "Country is required.";
    if (!form.state_id) newErrors.state_id = "State is required.";
    if (!form.city) newErrors.city = "City is required.";

    if (!form.zip || !/^\d{5,10}$/.test(form.zip))
      newErrors.zip = "Zip must be numeric and 5-10 digits.";

    if (!form.address || form.address.trim().length < 5)
      newErrors.address = "Address must be at least 5 characters.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const userId = localStorage.getItem("userId");
    const payload = {
      user_id: Number(userId),
      country_id: Number(form.country_id),
      state_id: Number(form.state_id),
      city_id: Number(form.city) || null,
      zip_code: form.zip,
      address: form.address,
      is_default: form.isDefault,
      name: form.fullName,
    };

    try {
      const response = await authFetch("user-address/add-address", {
        method: "POST",
        body: JSON.stringify(payload),
        headers: { "Content-Type": "application/json" },
      });
      const data = await response.json();
      if (data.success) {
        onSave?.(data);
        onClose?.();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await authFetch("sap-countries");
      const data = await response.json();
      const countriesData = data.data;
      setCountries(countriesData);
      const india = countriesData.find((c) => c.country_name === "India");
      if (india) setForm((f) => ({ ...f, country_id: india.id }));
    } catch (err) {
      console.log(err);
    }
  };

  const fetchStates = async (countryId) => {
    try {
      const response = await authFetch("sap-states", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          country_id: countryId,
        }),
      });
      const data = await response.json();
      const statesData = data.data;
      setStates(statesData);
      setForm((f) => ({ ...f, state_id: "", city: "" }));
    } catch (err) {
      console.log(err);
    }
  };

  const fetchCities = async (stateId, countryId) => {
    try {
      const response = await authFetch("sap-cities", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ state_id: stateId, country_id: countryId }),
      });
      const data = await response.json();
      setCities(data.data);
      setForm((f) => ({ ...f, city: "" }));
    } catch (err) {
      console.log(err);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div
        ref={dialogRef}
        className="relative mx-4 w-full max-w-3xl rounded-2xl bg-white p-6 shadow-xl ring-1 ring-purple-200"
      >
        <div className="mb-4 flex items-start justify-between">
          <h2 className="text-[24px] font-normal leading-[100%] text-[#572575] font-siromi">
            Add Billing Address
          </h2>
          <button
            onClick={onClose}
            className="ml-4 inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
          >
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 font-altisunisa">
          {/* Full Name */}
          <div>
            <label className="mb-1 block text-[12px] text-gray-700">
              Full Name<span className="text-rose-600">*</span>
            </label>
            <input
              type="text"
              name="fullName"
              value={form.fullName}
              onChange={handleChange}
              placeholder="Enter Your Name"
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#572575] focus:ring-2 focus:ring-[#572575]"
            />
            {errors.fullName && (
              <p className="text-rose-600 text-[12px] mt-1">
                {errors.fullName}
              </p>
            )}
          </div>

          {/* Country */}
          <div>
            <label className="mb-1 block text-[12px] text-gray-700">
              Country<span className="text-rose-600">*</span>
            </label>
            <select
              name="country_id"
              value={form.country_id}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#572575] focus:ring-2 focus:ring-[#572575]"
            >
              <option value="">Select Country</option>
              {countries.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.country_name}
                </option>
              ))}
            </select>
            {errors.country_id && (
              <p className="text-rose-600 text-[12px] mt-1">
                {errors.country_id}
              </p>
            )}
          </div>

          {/* State */}
          <div>
            <label className="mb-1 block text-[12px] text-gray-700">
              State / Union Territory<span className="text-rose-600">*</span>
            </label>
            <select
              name="state_id"
              value={form.state_id}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#572575] focus:ring-2 focus:ring-[#572575]"
            >
              <option value="">Select State / UT</option>
              {states.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.state_name}
                </option>
              ))}
            </select>
            {errors.state_id && (
              <p className="text-rose-600 text-[12px] mt-1">
                {errors.state_id}
              </p>
            )}
          </div>

          {/* City */}
          <div>
            <label className="mb-1 block text-[12px] text-gray-700">
              City<span className="text-rose-600">*</span>
            </label>
            <select
              name="city"
              value={form.city}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#572575] focus:ring-2 focus:ring-[#572575]"
            >
              <option value="">Select City</option>
              {cities.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.city_name}
                </option>
              ))}
            </select>
            {errors.city && (
              <p className="text-rose-600 text-[12px] mt-1">{errors.city}</p>
            )}
          </div>

          {/* Zip */}
          <div>
            <label className="mb-1 block text-[12px] text-gray-700">
              Zip / Postal Code<span className="text-rose-600">*</span>
            </label>
            <input
              type="text"
              name="zip"
              value={form.zip}
              onChange={handleChange}
              placeholder="Enter Zip Code"
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#572575] focus:ring-2 focus:ring-[#572575]"
            />
            {errors.zip && (
              <p className="text-rose-600 text-[12px] mt-1">{errors.zip}</p>
            )}
          </div>

          {/* Address */}
          <div>
            <label className="mb-1 block text-[12px] text-gray-700">
              Street Address<span className="text-rose-600">*</span>
            </label>
            <input
              type="text"
              name="address"
              value={form.address}
              onChange={handleChange}
              placeholder="Enter Your Address"
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#572575] focus:ring-2 focus:ring-[#572575]"
            />
            {errors.address && (
              <p className="text-rose-600 text-[12px] mt-1">{errors.address}</p>
            )}
          </div>

          <div className="flex items-center justify-between ">
            {" "}
            {/* Default checkbox */}{" "}
            <label className="mt-2 flex cursor-pointer items-center gap-3 text-base text-[#572575]">
              {" "}
              <input
                type="checkbox"
                name="isDefault"
                checked={form.isDefault}
                onChange={handleChange}
                className="h-5 w-5 rounded border-gray-300 text-[#572575] accent-[#572575]"
              />{" "}
              <span>Set as default address</span>{" "}
            </label>{" "}
            {/* Actions */}{" "}
            <div className="mt-4 flex justify-end">
              {" "}
              <button
                type="submit"
                className="inline-flex items-center rounded-md bg-[#9D0385] px-5 py-2.5 text-white text-[14px] shadow-sm hover:bg-[#9D0385] focus:outline-none focus:ring-2 focus:ring-[#9D0385]"
              >
                {" "}
                Save Address{" "}
              </button>{" "}
            </div>{" "}
          </div>
        </form>
      </div>
    </div>
  );
}
